"use client";

import { useEffect, useRef } from "react";
import { ExternalLink, Minus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { EappLaunchCardData } from "../../types/insurabot-cards";
import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";

const S =
  "rgba(0,0,0,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.03) 0px 1px 2px, rgba(0,0,0,0.02) 0px 4px 12px -2px";

/**
 * Rendered when InsuraBot has just initiated an eApplication.
 *
 * Iframe-mode carriers (Techficient) hand the launchUrl to the global
 * EnrollmentFloater so the iframe pops up automatically — and stays open
 * across page navigation if the agent chooses to float it.
 *
 * Popup-mode carriers (drop tickets / frame-blocking eApps) launch an
 * OAuth-style browser window instead — and ONLY on the agent's click,
 * because popup blockers eat window.open calls that don't originate from
 * a user gesture. No auto-open for those.
 */
export default function EappLaunchCard({ data }: { data: EappLaunchCardData }) {
  const floater = useEnrollmentFloater();
  const autoOpenedRef = useRef(false);
  const isPopup = data.launchMode === "popup";

  const session = {
    caseId: data.caseId,
    launchUrl: data.launchUrl,
    carrier: data.carrier,
    product: data.product,
    launchMode: data.launchMode,
  };

  const openApp = (opts?: { floating?: boolean }) => {
    const ok = floater.launch(session, opts);
    if (!ok) {
      toast.error("Popup blocked", {
        description:
          "Your browser blocked the application window. Allow popups for this site, then click Open again.",
      });
    }
  };

  // Auto-launch on first render — the AI just said "starting your application".
  // Skipped for popup-mode carriers: a popup opened outside a click gesture
  // gets silently killed by the browser, so those wait for the Open button.
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!data.launchUrl) return;
    if (data.autoOpen === false) return;
    if (isPopup) return;
    autoOpenedRef.current = true;
    floater.launch({
      caseId: data.caseId,
      launchUrl: data.launchUrl,
      carrier: data.carrier,
      product: data.product,
      launchMode: data.launchMode,
    });
  }, [data, floater, isPopup]);

  if (!data?.launchUrl) return null;

  return (
    <div className="mt-3 rounded-lg overflow-hidden" style={{ boxShadow: S }}>
      <div className="bg-gradient-to-br from-emerald-50 to-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-[0.1em] mb-1 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            Application started
          </p>
          <p className="text-[12px] font-semibold text-gray-900 truncate">
            {data.carrier ?? "Carrier"} — {data.product ?? "Product"}
          </p>
          <p className="text-[10px] text-gray-500 truncate">
            Case {data.caseId.slice(0, 8)}…
            {isPopup && " · opens in a secure carrier window"}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => openApp()}
            className="text-[11px] font-semibold rounded-md px-3 py-2 bg-gray-900 text-white flex items-center gap-1.5 hover:bg-gray-800"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open
          </button>
          {/* Float only applies to embedded iframes — a popup already IS a
              separate window, so offering to float it would be nonsense. */}
          {!isPopup && (
            <button
              type="button"
              onClick={() => openApp({ floating: true })}
              className="text-[10px] font-semibold rounded-md px-2 py-1 bg-white border text-gray-700 flex items-center justify-center gap-1 hover:bg-gray-50"
              title="Open as a draggable floating window — stays put while you navigate around InsuraCentral"
            >
              <Minus className="h-3 w-3" />
              Float
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
